import API_BASE from '../../config';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const API_URL = API_BASE;

// ── Color palette ─────────────────────────────────────────────────────────────
const C = {
  primary: '#1A6FD6',
  navy: '#0D1F3C',
  body: '#6B7280',
  border: '#E5E9F0',
  bg: '#FFFBEB',
  white: '#ffffff',
  sidebar: '#0F172A',
};

// ── Stage config ──────────────────────────────────────────────────────────────
const STAGE_CONFIG = {
  intake_done: { label: 'Intake Done', bg: '#FEF9C3', color: '#854D0E', border: '#FDE68A' },
  recruiter_assigned: { label: 'Assigned', bg: '#DBEAFE', color: '#1E40AF', border: '#BFDBFE' },
  expert_qa: { label: 'Expert QA', bg: '#EDE9FE', color: '#5B21B6', border: '#DDD6FE' },
  delivered: { label: 'Delivered', bg: '#DCFCE7', color: '#14532D', border: '#BBF7D0' },
  ai_rewrite: { label: 'AI Rewrite', bg: '#E0F2FE', color: '#0C4A6E', border: '#BAE6FD' },
  expert_review: { label: 'Expert Review', bg: '#EDE9FE', color: '#5B21B6', border: '#DDD6FE' },
  final_fix: { label: 'Final Fix', bg: '#FEF3C7', color: '#92400E', border: '#FDE68A' },
  recruiter_cert: { label: 'Recruiter Cert', bg: '#F0FDF4', color: '#166534', border: '#BBF7D0' },
};

const getStageCfg = (stage) =>
  STAGE_CONFIG[stage] || { label: stage || 'Unknown', bg: '#F3F4F6', color: '#374151', border: '#E5E7EB' };

// ── Helpers ───────────────────────────────────────────────────────────────────
const formatDateRange = (start, end, isCurrent) => {
  const fmt = (d) => {
    if (!d) return '';
    const date = new Date(d);
    return date.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
  };
  const s = fmt(start) || '—';
  const e = isCurrent ? 'Present' : fmt(end) || 'Present';
  return `${s} – ${e}`;
};

const truncate = (str, maxLen) => {
  if (!str) return '';
  if (str.length <= maxLen) return str;
  return str.slice(0, maxLen);
};

// ── Sub-components ────────────────────────────────────────────────────────────

const StageBadge = ({ stage }) => {
  const cfg = getStageCfg(stage);
  return (
    <span style={{
      display: 'inline-block',
      padding: '3px 10px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '600',
      background: cfg.bg,
      color: cfg.color,
      border: `1px solid ${cfg.border}`,
      whiteSpace: 'nowrap',
    }}>
      {cfg.label}
    </span>
  );
};

const SectionHeading = ({ children }) => (
  <div style={{
    fontSize: '11px',
    fontWeight: '700',
    color: '#9CA3AF',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginBottom: '10px',
    marginTop: '20px',
  }}>
    {children}
  </div>
);

const InfoRow = ({ label, value }) => {
  if (!value) return null;
  return (
    <div style={{ marginBottom: '8px', display: 'flex', flexDirection: 'column', gap: '1px' }}>
      <span style={{ fontSize: '11px', color: '#9CA3AF', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </span>
      <span style={{ fontSize: '13.5px', color: C.navy, fontWeight: '500', wordBreak: 'break-word' }}>
        {value}
      </span>
    </div>
  );
};

const Chip = ({ children, color = C.primary, bg = '#EFF6FF', border = '#BFDBFE' }) => (
  <span style={{
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '500',
    color,
    background: bg,
    border: `1px solid ${border}`,
    marginRight: '6px',
    marginBottom: '6px',
  }}>
    {children}
  </span>
);

// ── Main component ────────────────────────────────────────────────────────────
const RecruiterOrderDetail = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [aboutExpanded, setAboutExpanded] = useState(false);

  const handleUnauthorized = useCallback(() => {
    sessionStorage.removeItem('recruiter_auth');
    navigate('/recruiter/login');
  }, [navigate]);

  useEffect(() => {
    if (sessionStorage.getItem('recruiter_auth') !== '1') {
      navigate('/recruiter/login');
      return;
    }

    const fetchDetail = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/recruiter-portal/orders/${orderId}`, {
          credentials: 'include',
        });
        if (res.status === 401) { handleUnauthorized(); return; }
        const json = await res.json();
        if (!res.ok) throw new Error(json.detail || 'Failed to load order.');
        setData(json);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [orderId, navigate, handleUnauthorized]);

  // ── Loading ──────────────────────────────────────────────────
  if (loading) {
    return (
      <>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } } * { box-sizing: border-box; } body { margin: 0; }`}</style>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          minHeight: '100vh', background: C.bg, flexDirection: 'column', gap: '12px',
          fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
        }}>
          <div style={{
            width: '28px', height: '28px',
            border: `3px solid ${C.border}`, borderTopColor: C.primary,
            borderRadius: '50%', animation: 'spin 0.7s linear infinite',
          }} />
          <span style={{ fontSize: '14px', color: C.body }}>Loading order…</span>
        </div>
      </>
    );
  }

  // ── Error ────────────────────────────────────────────────────
  if (error) {
    return (
      <>
        <style>{`* { box-sizing: border-box; } body { margin: 0; }`}</style>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          minHeight: '100vh', background: C.bg, flexDirection: 'column', gap: '16px',
          fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
        }}>
          <div style={{
            padding: '24px 28px', borderRadius: '12px', background: '#FEF2F2',
            border: '1px solid #FECACA', color: '#DC2626', fontSize: '14px', textAlign: 'center', maxWidth: '360px',
          }}>
            <div style={{ fontWeight: '700', marginBottom: '6px' }}>Could not load order</div>
            <div>{error}</div>
          </div>
          <button
            style={{ padding: '9px 20px', borderRadius: '8px', background: C.primary, color: '#fff', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}
            onClick={() => navigate('/recruiter/dashboard')}
          >
            ← Back to Dashboard
          </button>
        </div>
      </>
    );
  }

  if (!data) return null;

  const { order, linkedin_profile, magic_link_token, profile_version } = data;

  // Extract fields from order
  const intake = order?.intake_data || {};
  const customerName = order?.customer_name || order?.user_name || intake?.name || '—';
  const customerEmail = order?.user_email || intake?.email || '—';
  const targetRole = order?.target_role || intake?.target_role || '—';
  const targetIndustry = order?.target_industry || intake?.target_industry || '—';
  const seniority = order?.seniority || intake?.seniority || '—';
  const yearsExp = order?.years_experience ?? intake?.years_experience ?? null;

  // Dream companies
  const dreamCompanies = intake?.dream_companies || order?.dream_companies || [];

  // Achievements
  let achievements = [];
  if (Array.isArray(intake?.achievements) && intake.achievements.length) {
    achievements = intake.achievements;
  } else {
    ['achievement_1', 'achievement_2', 'achievement_3'].forEach((key) => {
      const v = order?.[key] || intake?.[key];
      if (v) achievements.push(v);
    });
  }

  // Skills
  const skillsText = order?.skills || intake?.skills || '';

  // LinkedIn
  const lp = linkedin_profile;
  const lpAbout = lp?.about || lp?.summary || '';
  const lpAboutShort = truncate(lpAbout, 200);
  const lpNeedsExpand = lpAbout.length > 200;
  const lpExperiences = lp?.experiences || [];
  const lpSkills = Array.isArray(lp?.skills) ? lp.skills.slice(0, 10) : [];

  // Profile version
  const pvHeadline = profile_version?.headline || profile_version?.data?.headline || '';
  const pvAbout = profile_version?.about || profile_version?.data?.about || '';

  // Notes
  const notes = order?.recruiter_notes || '';

  // Back button style
  const backBtn = {
    display: 'inline-flex', alignItems: 'center', gap: '7px',
    padding: '8px 16px', borderRadius: '8px',
    background: '#fff', border: `1px solid ${C.border}`,
    color: C.navy, fontSize: '13.5px', fontWeight: '600',
    cursor: 'pointer', marginBottom: '24px',
    transition: 'background 0.1s',
    textDecoration: 'none',
  };

  return (
    <>
      <link
        href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&family=Fraunces:wght@700&display=swap"
        rel="stylesheet"
      />
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        * { box-sizing: border-box; }
        body { margin: 0; }
      `}</style>

      <div style={{
        minHeight: '100vh',
        background: C.bg,
        fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
        padding: '28px 32px',
      }}>
        {/* Top bar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <button
            style={backBtn}
            onClick={() => navigate('/recruiter/dashboard')}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#F1F5F9'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
          >
            <svg width="14" height="14" fill="none" viewBox="0 0 16 16">
              <path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Dashboard
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{
              fontFamily: 'monospace', fontWeight: '700', fontSize: '13px',
              background: '#fff', border: `1px solid ${C.border}`,
              padding: '5px 12px', borderRadius: '8px', color: C.navy,
            }}>
              #{order?.order_id || order?.id?.slice(0, 8)}
            </span>
            <StageBadge stage={order?.stage} />
          </div>
        </div>

        {/* Two-column layout */}
        <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>

          {/* ── LEFT PANEL (35%) ── */}
          <div style={{
            width: '35%',
            flexShrink: 0,
            background: C.white,
            borderRadius: '14px',
            border: `1px solid ${C.border}`,
            padding: '24px',
            position: 'sticky',
            top: '20px',
            maxHeight: 'calc(100vh - 40px)',
            overflowY: 'auto',
          }}>
            <div style={{ fontSize: '16px', fontWeight: '700', color: C.navy, marginBottom: '4px' }}>
              Customer Brief
            </div>
            <div style={{ fontSize: '12px', color: C.body, marginBottom: '20px' }}>
              Context for this profile review
            </div>

            {/* Customer */}
            <SectionHeading>Customer</SectionHeading>
            <InfoRow label="Name" value={customerName} />
            <InfoRow label="Email" value={customerEmail} />
            <InfoRow label="Target Role" value={targetRole} />
            <InfoRow label="Target Industry" value={targetIndustry} />
            <InfoRow label="Seniority" value={seniority} />
            {yearsExp !== null && yearsExp !== undefined && (
              <InfoRow label="Experience" value={`${yearsExp} years`} />
            )}

            {/* Dream Companies */}
            {dreamCompanies.length > 0 && (
              <>
                <SectionHeading>Dream Companies</SectionHeading>
                <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                  {dreamCompanies.map((company, i) => (
                    <Chip
                      key={i}
                      color="#1E40AF"
                      bg="#EFF6FF"
                      border="#BFDBFE"
                    >
                      {company}
                    </Chip>
                  ))}
                </div>
              </>
            )}

            {/* Achievements */}
            {achievements.length > 0 && (
              <>
                <SectionHeading>Achievements</SectionHeading>
                <ol style={{ margin: '0', padding: '0 0 0 18px' }}>
                  {achievements.map((ach, i) => (
                    <li key={i} style={{ fontSize: '13px', color: C.navy, marginBottom: '8px', lineHeight: '1.5' }}>
                      {ach}
                    </li>
                  ))}
                </ol>
              </>
            )}

            {/* Skills */}
            {skillsText && (
              <>
                <SectionHeading>Skills</SectionHeading>
                <p style={{ fontSize: '13px', color: C.navy, lineHeight: '1.6', margin: '0' }}>
                  {skillsText}
                </p>
              </>
            )}

            {/* LinkedIn Profile */}
            {lp && (
              <>
                <SectionHeading>LinkedIn Profile</SectionHeading>

                {/* Header */}
                <div style={{
                  background: '#FFFBEB',
                  border: `1px solid ${C.border}`,
                  borderRadius: '10px',
                  padding: '14px',
                  marginBottom: '12px',
                }}>
                  <div style={{ fontWeight: '700', fontSize: '14px', color: C.navy }}>
                    {lp.full_name || lp.name || '—'}
                  </div>
                  {lp.headline && (
                    <div style={{ fontSize: '12.5px', color: C.body, marginTop: '2px', lineHeight: '1.4' }}>
                      {lp.headline}
                    </div>
                  )}
                  {lp.location && (
                    <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <svg width="11" height="11" fill="none" viewBox="0 0 16 16">
                        <path d="M8 2a4.5 4.5 0 00-4.5 4.5C3.5 10 8 14 8 14s4.5-4 4.5-7.5A4.5 4.5 0 008 2z" stroke="currentColor" strokeWidth="1.5" />
                        <circle cx="8" cy="6.5" r="1.5" stroke="currentColor" strokeWidth="1.3" />
                      </svg>
                      {lp.location}
                    </div>
                  )}
                </div>

                {/* About */}
                {lpAbout && (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
                      About
                    </div>
                    <p style={{ fontSize: '13px', color: C.navy, lineHeight: '1.6', margin: '0' }}>
                      {aboutExpanded ? lpAbout : lpAboutShort}
                      {lpNeedsExpand && !aboutExpanded && '…'}
                    </p>
                    {lpNeedsExpand && (
                      <button
                        style={{ background: 'none', border: 'none', color: C.primary, cursor: 'pointer', fontSize: '12px', fontWeight: '600', padding: '4px 0' }}
                        onClick={() => setAboutExpanded((v) => !v)}
                      >
                        {aboutExpanded ? 'show less' : 'read more'}
                      </button>
                    )}
                  </div>
                )}

                {/* Work Experiences */}
                {lpExperiences.length > 0 && (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>
                      Experience
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {lpExperiences.map((exp, i) => (
                        <div key={i} style={{
                          padding: '12px',
                          borderRadius: '9px',
                          border: `1px solid ${C.border}`,
                          background: '#FAFBFF',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
                            <div>
                              <div style={{ fontSize: '13px', fontWeight: '700', color: C.navy }}>
                                {exp.title || '—'}
                              </div>
                              <div style={{ fontSize: '12.5px', color: C.body, marginTop: '1px' }}>
                                {exp.company || exp.company_name || '—'}
                              </div>
                            </div>
                            {(exp.is_current || exp.currently_working) && (
                              <span style={{
                                padding: '2px 8px', borderRadius: '20px',
                                fontSize: '10px', fontWeight: '700',
                                background: '#DCFCE7', color: '#15803D',
                                border: '1px solid #BBF7D0', whiteSpace: 'nowrap', flexShrink: 0,
                              }}>
                                Current
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: '11.5px', color: '#9CA3AF', marginTop: '4px' }}>
                            {formatDateRange(exp.start_date, exp.end_date, exp.is_current || exp.currently_working)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* LinkedIn Skills */}
                {lpSkills.length > 0 && (
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>
                      Top Skills
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                      {lpSkills.map((skill, i) => (
                        <Chip key={i} color="#374151" bg="#F9FAFB" border="#E5E7EB">
                          {skill}
                        </Chip>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* ── RIGHT PANEL (65%) ── */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' }}>

            {/* Review Section */}
            <div style={{
              background: C.white,
              borderRadius: '14px',
              border: `1px solid ${C.border}`,
              padding: '28px',
            }}>
              <div style={{ fontSize: '16px', fontWeight: '700', color: C.navy, marginBottom: '4px' }}>
                Your Review
              </div>
              <div style={{ fontSize: '13px', color: C.body, marginBottom: '24px' }}>
                Review the candidate's AI-generated LinkedIn profile and certify it.
              </div>

              {magic_link_token ? (
                <div>
                  <a
                    href={`${window.location.origin}/review/${magic_link_token}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '14px 28px',
                      borderRadius: '10px',
                      background: C.primary,
                      color: '#fff',
                      fontWeight: '700',
                      fontSize: '15px',
                      textDecoration: 'none',
                      transition: 'background 0.15s',
                      boxShadow: '0 2px 8px rgba(26,111,214,0.25)',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#155FB8'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = C.primary; }}
                  >
                    <svg width="18" height="18" fill="none" viewBox="0 0 24 24">
                      <path d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Open Review Page →
                  </a>
                  <p style={{ fontSize: '12.5px', color: C.body, marginTop: '12px', lineHeight: '1.5' }}>
                    Opens the full profile review in a new tab. Review, edit if needed, and certify when satisfied.
                  </p>
                </div>
              ) : (
                <div style={{
                  padding: '20px 24px',
                  borderRadius: '10px',
                  background: '#F9FAFB',
                  border: `1px solid ${C.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                }}>
                  <svg width="36" height="36" fill="none" viewBox="0 0 24 24" style={{ flexShrink: 0, color: '#D1D5DB' }}>
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
                    <path d="M12 8v5M12 16h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#6B7280', marginBottom: '2px' }}>
                      Review link not yet generated
                    </div>
                    <div style={{ fontSize: '13px', color: '#9CA3AF' }}>
                      Contact the Successlane team to generate your review link for this order.
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Notes */}
            <div style={{
              background: C.white,
              borderRadius: '14px',
              border: `1px solid ${C.border}`,
              padding: '24px',
            }}>
              <div style={{ fontSize: '15px', fontWeight: '700', color: C.navy, marginBottom: '4px' }}>
                Notes
              </div>
              <div style={{ fontSize: '12.5px', color: C.body, marginBottom: '14px' }}>
                Context notes for this candidate. Use the review page to add or edit feedback.
              </div>
              <textarea
                readOnly
                value={notes}
                placeholder="No notes recorded for this order yet."
                style={{
                  width: '100%',
                  minHeight: '100px',
                  padding: '12px 14px',
                  borderRadius: '10px',
                  border: `1.5px solid ${C.border}`,
                  background: '#FFFBEB',
                  fontSize: '13.5px',
                  color: C.navy,
                  lineHeight: '1.6',
                  resize: 'vertical',
                  outline: 'none',
                  fontFamily: 'inherit',
                  cursor: 'default',
                }}
              />
              <div style={{ fontSize: '11.5px', color: '#9CA3AF', marginTop: '6px' }}>
                Editing is done via the magic review link above.
              </div>
            </div>

            {/* AI Profile Version */}
            {(pvHeadline || pvAbout) && (
              <div style={{
                background: C.white,
                borderRadius: '14px',
                border: `1px solid ${C.border}`,
                padding: '24px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                  <div style={{ fontSize: '15px', fontWeight: '700', color: C.navy }}>
                    AI-Generated Profile Draft
                  </div>
                  <span style={{
                    padding: '2px 9px', borderRadius: '20px',
                    fontSize: '11px', fontWeight: '700',
                    background: '#EDE9FE', color: '#5B21B6',
                    border: '1px solid #DDD6FE',
                  }}>
                    Draft
                  </span>
                </div>

                {pvHeadline && (
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
                      Headline
                    </div>
                    <div style={{
                      padding: '12px 14px',
                      borderRadius: '9px',
                      border: `1px solid ${C.border}`,
                      background: '#FAFBFF',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: C.navy,
                      lineHeight: '1.5',
                    }}>
                      {pvHeadline}
                    </div>
                  </div>
                )}

                {pvAbout && (
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
                      About / Summary
                      <span style={{ fontSize: '10px', color: '#C4B5FD', marginLeft: '6px', textTransform: 'none', fontWeight: '500' }}>
                        (preview — first 300 chars)
                      </span>
                    </div>
                    <div style={{
                      padding: '14px',
                      borderRadius: '9px',
                      border: `1px solid ${C.border}`,
                      background: '#FAFBFF',
                      fontSize: '13.5px',
                      color: C.navy,
                      lineHeight: '1.65',
                    }}>
                      {truncate(pvAbout, 300)}
                      {pvAbout.length > 300 && (
                        <span style={{ color: '#A78BFA' }}>… (see full profile on review page)</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default RecruiterOrderDetail;
